import { useState, useEffect, useRef, useCallback } from 'react'
import { Bot, MessageCircle, ArrowLeft, Send, StopCircle, Loader2, Trash2, User, Pencil, Plus, Upload, X, Search, Code, Brain, Globe, FileSearch, Palette, Music, Video, Image, Settings, Sparkles, Zap, Terminal, BookOpen, BarChart, Shield, Cpu, Paperclip, Camera, Link, Mic, Copy, RefreshCw, Volume2, ChevronDown, GripVertical, Lock } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import client from '../api/client'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import { useTokenStore } from '../store/tokenStore'
import ChatSettingsPanel from '../components/ChatSettings'

// ─── Types ────────────────────────────────────────────────

interface AgentSkill {
  name: string
  icon: string // имя иконки
  description: string
}

interface AgentInfo {
  id: string
  name: string
  emoji: string
  color: string
  description: string
  long_description: string
  system_prompt: string
  avatar_url?: string
  skills: AgentSkill[]
  tools?: AgentToolInfo[]
  model?: string
  provider?: string
  is_global: boolean
  created_at?: string
}

interface AgentToolInfo {
  id: string
  name: string
  description: string
  is_active: boolean
}

interface ChatMessage {
  id: string
  role: string
  content: string
  tokens: number
  model: string | null
  timestamp: string
}

// ─── Skill Icons ──────────────────────────────────────────

const SKILL_ICONS: Record<string, any> = {
  code: Code,
  brain: Brain,
  globe: Globe,
  search: FileSearch,
  palette: Palette,
  music: Music,
  video: Video,
  image: Image,
  settings: Settings,
  sparkles: Sparkles,
  zap: Zap,
  terminal: Terminal,
  book: BookOpen,
  chart: BarChart,
  shield: Shield,
  cpu: Cpu,
  message: MessageCircle,
  bot: Bot,
}

function SkillIcon({ name, className }: { name: string; className?: string }) {
  const Icon = SKILL_ICONS[name] || Bot
  return <Icon className={className || 'w-3.5 h-3.5'} />
}

// ─── Agent Colors ─────────────────────────────────────────

const AGENT_COLORS: Record<string, string> = {
  indigo: 'from-indigo-500/20 to-indigo-600/10 border-indigo-500/30',
  blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
  emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
  amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
  purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
  red: 'from-red-500/20 to-red-600/10 border-red-500/30',
  pink: 'from-pink-500/20 to-pink-600/10 border-pink-500/30',
  cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30',
  rose: 'from-rose-500/20 to-rose-600/10 border-rose-500/30',
  teal: 'from-teal-500/20 to-teal-600/10 border-teal-500/30',
  orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30',
  violet: 'from-violet-500/20 to-violet-600/10 border-violet-500/30',
}

// ─── Fallback agents data ────────────────────────────────

const FALLBACK_AGENTS: AgentInfo[] = [
  {
    id: 'cortex',
    name: 'Cortex',
    emoji: '🧠',
    color: 'indigo',
    description: 'Универсальный AI-ассистент с широким спектром навыков',
    long_description: 'Многофункциональный агент, способный выполнять задачи любой сложности: от написания кода до анализа данных и креативного письма. Адаптируется под стиль общения пользователя.',
    system_prompt: 'Ты — Cortex, мощный AI-ассистент. Ты умеешь писать код, анализировать данные, создавать контент и помогать с любыми задачами. Отвечай полезно, структурированно и дружелюбно.',
    skills: [
      { name: 'code', icon: 'code', description: 'Написание и ревью кода' },
      { name: 'brain', icon: 'brain', description: 'Анализ данных и аналитика' },
      { name: 'globe', icon: 'globe', description: 'Веб-поиск и исследование' },
      { name: 'search', icon: 'search', description: 'Поиск и систематизация информации' },
    ],
    is_global: true,
  },
  {
    id: 'seo-optimizer',
    name: 'SEO-Оптимизатор',
    emoji: '🔍',
    color: 'emerald',
    description: 'Анализирует мета-теги, контент и структуру для SEO',
    long_description: 'Продвинутый SEO-агент, который анализирует страницы сайтов, проверяет мета-теги, заголовки, структурированные данные, скорость загрузки и даёт рекомендации по улучшению поисковой выдачи.',
    system_prompt: 'Ты — SEO-оптимизатор. Анализируй веб-страницы, проверь мета-теги (title, description, h1-h6), Open Graph, structured data (JSON-LD), качество контента. Давай конкретные actionable рекомендации.',
    skills: [
      { name: 'globe', icon: 'globe', description: 'Анализ мета-тегов' },
      { name: 'search', icon: 'search', description: 'SEO-аудит страниц' },
      { name: 'code', icon: 'code', description: 'JSON-LD структуры' },
      { name: 'book', icon: 'book', description: 'Контент-анализ' },
    ],
    is_global: true,
  },
  {
    id: 'code-reviewer',
    name: 'Code Reviewer',
    emoji: '💻',
    color: 'blue',
    description: 'Проверяет код, находит баги и предлагает оптимизации',
    long_description: 'Профессиональный ревьюер кода с опытом в Python, JavaScript, TypeScript, Go, Rust и других языках. Анализирует архитектуру, производительность, безопасность и стиль кода.',
    system_prompt: 'Ты — опытный code reviewer. Анализируй код на: баги, утечки памяти, проблемы безопасности, производительность, читаемость, соответствие best practices. Давай конкретные исправления.',
    skills: [
      { name: 'code', icon: 'code', description: 'Python, JS, TS, Go, Rust' },
      { name: 'shield', icon: 'shield', description: 'Безопасность кода' },
      { name: 'zap', icon: 'zap', description: 'Оптимизация производительности' },
      { name: 'cpu', icon: 'cpu', description: 'Архитектурный анализ' },
    ],
    is_global: true,
  },
  {
    id: 'content-writer',
    name: 'Content Writer',
    emoji: '✍️',
    color: 'purple',
    description: 'Пишет статьи, посты, сценарии и креативные тексты',
    long_description: 'Креативный копирайтер, создающий качественный контент любого формата: статьи для блога, посты для соцсетей, лендинги, email-рассылки, сценарии для видео.',
    system_prompt: 'Ты — профессиональный копирайтер. Создавай качественный контент: структурированные статьи, вовлекающие посты, продающие тексты. Учитывай ЦА, tone of voice и цели материала.',
    skills: [
      { name: 'book', icon: 'book', description: 'Статьи и блоги' },
      { name: 'sparkles', icon: 'sparkles', description: 'Креативное письмо' },
      { name: 'palette', icon: 'palette', description: 'Tone of voice' },
      { name: 'globe', icon: 'globe', description: 'SEO-тексты' },
    ],
    is_global: true,
  },
  {
    id: 'data-analyst',
    name: 'Data Analyst',
    emoji: '📊',
    color: 'amber',
    description: 'Анализирует данные, строит отчёты и находит инсайты',
    long_description: 'Агент по анализу данных. Работает с CSV, JSON, таблицами. Строит графики, находит корреляции, выявляет тренды и готовит отчёты с выводами и рекомендациями.',
    system_prompt: 'Ты — аналитик данных. Анализируй предоставленные данные: найди паттерны, аномалии, тренды. Строй понятные отчёты с выводами и визуализациями в markdown.',
    skills: [
      { name: 'chart', icon: 'chart', description: 'Статистический анализ' },
      { name: 'brain', icon: 'brain', description: 'Data mining' },
      { name: 'code', icon: 'code', description: 'SQL, Python' },
      { name: 'book', icon: 'book', description: 'Отчёты и дашборды' },
    ],
    is_global: true,
  },
  {
    id: 'image-prompt-artist',
    name: 'Prompt Artist',
    emoji: '🎨',
    color: 'pink',
    description: 'Создаёт промпты для генерации изображений',
    long_description: 'Эксперт по промпт-инжинирингу для нейросетей генерации изображений. Создаёт детальные промпты для Midjourney, Flux, DALL-E, Stable Diffusion и других моделей.',
    system_prompt: 'Ты — эксперт по промптам для генерации изображений. Создавай детальные, выразительные промпты. Учитывай стиль (реализм, аниме, 3D), освещение, композицию, цветовую гамму. Давай варианты.',
    skills: [
      { name: 'palette', icon: 'palette', description: 'Midjourney, Flux, SD' },
      { name: 'sparkles', icon: 'sparkles', description: 'Креативные промпты' },
      { name: 'image', icon: 'image', description: 'Композиция и стиль' },
      { name: 'settings', icon: 'settings', description: 'Параметры моделей' },
    ],
    is_global: true,
  },
  {
    id: 'devops-bot',
    name: 'DevOps Bot',
    emoji: '⚙️',
    color: 'cyan',
    description: 'Помогает с Docker, CI/CD, серверами и деплоем',
    long_description: 'Специалист по DevOps и инфраструктуре. Помогает с Docker, Docker Compose, GitHub Actions, Nginx, Linux, AWS/GCP/Cloudflare, мониторингом и деплоем.',
    system_prompt: 'Ты — DevOps-инженер. Помогай с: Docker (Dockerfile, compose), CI/CD (GitHub Actions, GitLab CI), Nginx, Linux администрированием, деплоем. Давай готовые конфиги и команды.',
    skills: [
      { name: 'terminal', icon: 'terminal', description: 'Docker, Linux' },
      { name: 'zap', icon: 'zap', description: 'CI/CD пайплайны' },
      { name: 'shield', icon: 'shield', description: 'Безопасность' },
      { name: 'globe', icon: 'globe', description: 'Cloudflare, AWS' },
    ],
    is_global: true,
  },
  {
    id: 'tech-writer',
    name: 'Tech Writer',
    emoji: '📝',
    color: 'teal',
    description: 'Пишет документацию, API-спеки и гайды',
    long_description: 'Профессиональный технический писатель. Создаёт документацию для API, README, руководства пользователя, архитектурные описания и технические спецификации.',
    system_prompt: 'Ты — технический писатель. Пиши: README, документацию API, архитектурные описания, гайды по установке и настройке. Используй чёткую структуру, примеры кода, схемы в markdown.',
    skills: [
      { name: 'book', icon: 'book', description: 'API-документация' },
      { name: 'code', icon: 'code', description: 'Примеры кода' },
      { name: 'globe', icon: 'globe', description: 'README и гайды' },
      { name: 'search', icon: 'search', description: 'Структурирование' },
    ],
    is_global: true,
  },
]

// ─── Agent Chat ───────────────────────────────────────────

function AgentChat({
  agent,
  onBack,
}: {
  agent: AgentInfo
  onBack: () => void
}) {
  const { token: authToken } = useAuthStore()
  const { balance } = useTokenStore()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [initLoading, setInitLoading] = useState(true)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const [showSettingsPanel, setShowSettingsPanel] = useState(false)
  const [files, setFiles] = useState<{name: string; base64: string}[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const recognitionRef = useRef<any>(null)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [speakingMsgId, setSpeakingMsgId] = useState<string | null>(null)
  const [webSearch, setWebSearch] = useState(false)
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null)
  const [editMessageId, setEditMessageId] = useState<string | null>(null)
  const [editMessageContent, setEditMessageContent] = useState('')
  const [cameraModal, setCameraModal] = useState(false)
  const streamRef = useRef<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showVoiceMenu, setShowVoiceMenu] = useState(false)
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([])
  const [selectedVoiceName, setSelectedVoiceName] = useState<string | null>(null)
  const [paidModelNames, setPaidModelNames] = useState<string[]>([])

  useEffect(() => {
    client.get('/models/ai-models').then(({data}) => {
      const paid = (data || []).filter((m: any) => m.category === 'chat' && m.is_paid)
      setPaidModelNames(paid.map((m: any) => m.model_name))
    }).catch(() => {})
  }, [])

  const SESSION_KEY = `agent_session_${agent.id}`

  // TODO: create/get chat session from API
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const cachedSessionId = localStorage.getItem(SESSION_KEY)
      if (cachedSessionId) {
        try {
          const { data } = await client.get(`/chat/${cachedSessionId}`)
          if (!cancelled && data) {
            const cleanMsgs = (data.messages || []).filter(
              (m: ChatMessage) => m.role !== 'assistant' || m.content?.trim()
            )
            setSessionId(data.id)
            setMessages(cleanMsgs)
            setInitLoading(false)
            return
          }
        } catch {
          localStorage.removeItem(SESSION_KEY)
        }
      }

      try {
        const { data } = await client.post('/chat', {
          model: agent.model || 'gpt-5.4-mini',
          title: `Чат с ${agent.name}`,
          agent_id: agent.id,
        })
        if (!cancelled) {
          setSessionId(data.id)
          localStorage.setItem(SESSION_KEY, data.id)
        }
      } catch {
        toast.error('Ошибка при создании чата')
      }
      if (!cancelled) setInitLoading(false)
    })()
    return () => { cancelled = true }
  }, [agent.id])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 260) + 'px'
    }
  }, [input])

  // Загрузка голосов
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices()
      if (voices.length > 0) {
        const ranked = voices
          .filter(v => v.lang.startsWith('ru') || v.lang.startsWith('en') || v.lang.startsWith('de') || v.lang.startsWith('fr') || v.lang.startsWith('es'))
          .sort((a, b) => {
            const aScore = /Microsoft|Google|Apple|Yandex/i.test(a.name) ? 3 : /Samantha|Karen|Daniel|Moira|Tessa/i.test(a.name) ? 2 : 1
            const bScore = /Microsoft|Google|Apple|Yandex/i.test(b.name) ? 3 : /Samantha|Karen|Daniel|Moira|Tessa/i.test(b.name) ? 2 : 1
            return bScore - aScore
          })
        setAvailableVoices(ranked)
        if (!selectedVoiceName && ranked.length > 0) {
          setSelectedVoiceName(ranked[0].name)
        }
      }
    }
    loadVoices()
    window.speechSynthesis.onvoiceschanged = loadVoices
    return () => { window.speechSynthesis.onvoiceschanged = null }
  }, [])

  // Голосовой ввод
  const startRecording = useCallback(() => {
    if (isRecording) {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
      setIsRecording(false)
      return
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      toast.error('Ваш браузер не поддерживает голосовой ввод')
      return
    }
    const recognition = new SpeechRecognition()
    recognition.lang = 'ru-RU'
    recognition.continuous = false
    recognition.interimResults = true
    recognition.maxAlternatives = 1
    recognition.onresult = (event: any) => {
      const results = event.results
      const lastResult = results[results.length - 1]
      if (lastResult && lastResult.isFinal) {
        const text = lastResult[0].transcript
        setInput(text)
      }
    }
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      if (event.error !== 'aborted') {
        toast.error('Ошибка распознавания речи')
      }
      setIsRecording(false)
    }
    recognition.onend = () => {
      setIsRecording(false)
    }
    recognitionRef.current = recognition
    recognition.start()
    setIsRecording(true)
  }, [isRecording])

  // Озвучивание текста
  const speakText = useCallback((text: string, msgId: string) => {
    if (isSpeaking && speakingMsgId === msgId) {
      window.speechSynthesis.cancel()
      setIsSpeaking(false)
      setSpeakingMsgId(null)
      return
    }
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text.replace(/[*#_\[\]()]/g, '').slice(0, 2000))
    utterance.lang = 'ru-RU'
    utterance.rate = 1.0
    utterance.pitch = 1.0
    const voices = window.speechSynthesis.getVoices()
    const ruVoice = voices.find(v => v.lang.startsWith('ru'))
    if (ruVoice) utterance.voice = ruVoice
    utterance.onstart = () => { setIsSpeaking(true); setSpeakingMsgId(msgId) }
    utterance.onend = () => { setIsSpeaking(false); setSpeakingMsgId(null) }
    utterance.onerror = () => { setIsSpeaking(false); setSpeakingMsgId(null) }
    window.speechSynthesis.speak(utterance)
  }, [isSpeaking, speakingMsgId])

  // Копирование в буфер
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Скопировано')
  }

  // Перегенерация ответа
  const regenerateMessage = (msg: ChatMessage) => {
    if (msg.role !== 'assistant') return
    const msgs = [...messages]
    let lastUserMsg: ChatMessage | null = null
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'user') {
        lastUserMsg = msgs[i]
        break
      }
    }
    if (!lastUserMsg) return
    setMessages(prev => prev.filter(m => m.id !== msg.id))
    // Устанавливаем content триггером для handleSend
    const text = lastUserMsg.content
    // Копируем логику handleSend для переотправки
    if (!sessionId) return
    setInput('')
    setLoading(true)
    setIsSending(true)

    const bodyObj: any = { content: text, model: agent.model || 'gpt-5.4-mini', agent_id: agent.id }

    const controller = new AbortController()
    abortRef.current = controller

    fetch('/api/chat/' + sessionId + '/message/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(bodyObj),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) {
        toast.error(`HTTP ${response.status}`)
        setLoading(false)
        return
      }
      const reader = response.body?.getReader()
      if (!reader) { setLoading(false); return }

      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''
      let assistantId: string | null = null
      let msgCreated = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i]
          if (line.startsWith('event: meta')) {
            const nl = lines[i + 1]
            if (nl?.startsWith('data: ')) {
              try {
                const meta = JSON.parse(nl.slice(6))
                if (meta.assistant_msg_id) assistantId = meta.assistant_msg_id
              } catch {}
            }
            i++
          } else if (line.startsWith('data: ')) {
            try {
              const payload = JSON.parse(line.slice(6))
              const chunkText = payload.content ?? payload.text
              if (chunkText !== undefined) {
                if (!msgCreated) {
                  msgCreated = true
                  setLoading(false)
                  fullContent = chunkText
                  const msgId = assistantId || ('stream-' + Date.now())
                  setMessages(prev => [...prev, { id: msgId, role: 'assistant', content: fullContent, tokens: 0, model: null, timestamp: new Date().toISOString() }])
                } else {
                  fullContent += chunkText
                  setMessages(prev =>
                    prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m)
                  )
                }
              }
            } catch {}
          }
        }
      }
    }).catch((e: any) => {
      if (e?.name !== 'AbortError') {
        toast.error(e?.message || 'Ошибка при отправке')
      }
      setLoading(false)
    }).finally(() => {
      setIsSending(false)
      abortRef.current = null
    })
  }

  // Удаление сообщения
  const deleteMessage = async (msgId: string) => {
    try {
      await client.delete(`/chat/message/${msgId}`)
      toast.success('Сообщение удалено')
      setMessages(prev => prev.filter(m => m.id !== msgId))
    } catch {
      // Если сервер не поддерживает удаление отдельных сообщений — удалим локально
      setMessages(prev => prev.filter(m => m.id !== msgId))
      toast.success('Сообщение удалено')
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList) return

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]
      const reader = new FileReader()
      reader.onload = (ev) => {
        const base64 = (ev.target?.result as string)?.split(',')[1] || ''
        setFiles(prev => [...prev, { name: file.name, base64 }])
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCaptureCamera = () => {
    if (navigator.mediaDevices?.getUserMedia) {
      setCameraModal(true)
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false
          })
          streamRef.current = stream
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (err) {
          console.error('Camera error:', err)
          setCameraModal(false)
          toast.error('Не удалось получить доступ к камере')
        }
      }, 100)
    } else {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.capture = 'environment'
      input.onchange = (e: any) => {
        if (e.target?.files?.length > 0) {
          handleFileSelect({ target: { files: e.target.files, value: '' } } as any)
        }
      }
      input.click()
    }
  }

  const handleAddPageLink = () => {
    const url = window.prompt('Введите ссылку на страницу:', 'https://')
    if (!url || !url.trim()) return
    setInput((prev) => {
      const trimmed = prev.trim()
      return trimmed ? `${trimmed}\n[Ссылка](${url.trim()})` : `[Ссылка](${url.trim()})`
    })
    inputRef.current?.focus()
  }

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text && files.length === 0) return
    if (!sessionId) return

    // Проверка — если у агента платная модель и баланс 0
    const agentModelPaid = paidModelNames.includes(agent.model)
    if (agentModelPaid && (balance ?? 0) < 1000) {
      toast.error('Недостаточно токенов. Пополните баланс для доступа к платным моделям.')
      return
    }

    setInput('')
    setLoading(true)
    setIsSending(true)

    const currentFiles = [...files]
    setFiles([])

    const displayContent = text || currentFiles.map(f => `[${f.name}]`).join(', ')
    const userMsg: ChatMessage = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: displayContent,
      tokens: 0,
      model: null,
      timestamp: new Date().toISOString(),
    }
    setMessages(prev => [...prev, userMsg])

    try {
      const bodyObj: any = { content: text, model: agent.model || 'gpt-5.4-mini', agent_id: agent.id }
      if (currentFiles.length > 0) {
        bodyObj.files = currentFiles.map(f => f.base64)
        bodyObj.filenames = currentFiles.map(f => f.name)
      }

      const controller = new AbortController()
      abortRef.current = controller

      const response = await fetch('/api/chat/' + sessionId + '/message/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify(bodyObj),
        signal: controller.signal,
      })

      if (!response.ok) {
        try {
          const errData = await response.json()
          toast.error(errData.detail || `HTTP ${response.status}`)
        } catch {
          toast.error(`HTTP ${response.status}`)
        }
        setLoading(false)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        setLoading(false)
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''
      let assistantId: string | null = null
      let msgCreated = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i]
          if (line.startsWith('event: meta')) {
            const nl = lines[i + 1]
            if (nl?.startsWith('data: ')) {
              try {
                const meta = JSON.parse(nl.slice(6))
                if (meta.assistant_msg_id) {
                  assistantId = meta.assistant_msg_id
                }
              } catch {}
            }
            i++
          } else if (line.startsWith('data: ')) {
            try {
              const payload = JSON.parse(line.slice(6))
              const chunkText = payload.content ?? payload.text
              if (chunkText !== undefined) {
                if (!msgCreated) {
                  msgCreated = true
                  setLoading(false)
                  fullContent = chunkText
                  const msgId = assistantId || ('stream-' + Date.now())
                  setMessages(prev => [...prev, { id: msgId, role: 'assistant', content: fullContent, tokens: 0, model: null, timestamp: new Date().toISOString() }])
                } else {
                  fullContent += chunkText
                  setMessages(prev =>
                    prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m)
                  )
                }
              }
            } catch {}
          }
        }
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        // нормальная отмена
      } else {
        toast.error(e?.message || 'Ошибка при отправке')
      }
      setLoading(false)
    } finally {
      setIsSending(false)
      abortRef.current = null
    }
  }, [input, sessionId, agent.id, files])

  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
    setLoading(false)
    setIsSending(false)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleClearDialogue = async () => {
    if (!sessionId) return
    try {
      await client.delete(`/chat/${sessionId}`)
    } catch {}
    localStorage.removeItem(SESSION_KEY)
    setMessages([])
    setSessionId(null)
    setInput('')
    try {
      const { data } = await client.post('/chat', {
        model: agent.model || 'gpt-5.4-mini',
        title: `Чат с ${agent.name}`,
        agent_id: agent.id,
      })
      setSessionId(data.id)
      localStorage.setItem(SESSION_KEY, data.id)
      toast.success('Диалог очищен')
    } catch {
      toast.error('Ошибка при создании нового диалога')
    }
  }

  const fileImportRef = useRef<HTMLInputElement>(null)

  const handleImportChats = () => {
    fileImportRef.current?.click()
  }

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    for (let i = 0; i < files.length; i++) {
      try {
        const text = await files[i].text()
        const data = JSON.parse(text)
        if (Array.isArray(data) && data.length > 0) {
          setMessages(data)
          toast.success(`Импортировано ${data.length} сообщений`)
        } else {
          toast.error('Файл не содержит сообщений')
        }
      } catch (err: any) {
        toast.error(err.message || 'Ошибка импорта')
      }
    }

    e.target.value = ''
  }

  if (initLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
      </div>
    )
  }

  const colorClass = AGENT_COLORS[agent.color] || AGENT_COLORS.indigo

  return (
    <div className="flex flex-col h-[calc(100vh-1rem)] max-w-4xl mx-auto">
      {/* Шапка */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
        <button
          onClick={onBack}
          className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        {agent.avatar_url ? (
          <img src={agent.avatar_url} alt="" className="w-10 h-10 rounded-xl object-cover border border-white/10" />
        ) : (
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center text-xl border border-white/10`}>
            {agent.emoji}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-white inline-flex items-center gap-1.5">
                  {agent.name}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                    agent.provider === 'typegpt'
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                      : 'bg-amber-500/20 text-amber-400 border border-amber-500/20'
                  }`}>
                    {agent.provider === 'typegpt' ? 'FREE' : '👑'}
                  </span>
                </h2>
          <p className="text-[11px] text-gray-500 truncate">{agent.description}</p>
        </div>
        {/* Скиллы в шапке */}
        <div className="hidden sm:flex items-center gap-1.5">
          {agent.skills.filter(s => !['book','globe','palette','sparkles'].includes(s.icon)).slice(0, 4).map((skill, i) => (
            <div
              key={i}
              className="px-2 py-1 rounded-lg bg-white/5 border border-white/5 flex items-center gap-1"
              title={skill.description}
            >
              <SkillIcon name={skill.icon} className="w-3 h-3 text-indigo-400" />
              <span className="text-[10px] text-gray-400">{skill.name}</span>
            </div>
          ))}
          {/* Инструменты в шапке */}
          {agent.tools && agent.tools.length > 0 && (
            <>
              <div className="w-px h-5 bg-white/10 mx-1" />
              {agent.tools.filter(t => !['analyze_text','extract_data','fetch_url'].includes(t.name)).slice(0, 3).map((tool, i) => (
                <div
                  key={i}
                  className="px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/10 flex items-center gap-1"
                  title={tool.description || tool.name}
                >
                  <Zap className="w-3 h-3 text-amber-400" />
                  <span className="text-[10px] text-amber-300/70">{tool.name}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Сообщения */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center text-4xl mx-auto mb-3 border border-white/10`}>
                {agent.emoji}
              </div>
              <p className="text-gray-400 text-sm">
                Чат с агентом <span className="text-white font-medium">{agent.name}</span>
              </p>
              <p className="text-gray-600 text-xs mt-1 max-w-md mx-auto">{agent.long_description}</p>
              {/* Скиллы на стартовом экране */}
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {agent.skills.filter(s => !['book','globe','palette','sparkles'].includes(s.icon)).map((skill, i) => (
                  <div
                    key={i}
                    className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/5 flex items-center gap-1.5"
                    title={skill.description}
                  >
                    <SkillIcon name={skill.icon} className="w-3.5 h-3.5 text-indigo-400" />
                    <span className="text-xs text-gray-400">{skill.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 group relative ${
              msg.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
            onMouseEnter={() => setHoveredMsgId(msg.id)}
            onMouseLeave={() => setHoveredMsgId(null)}
          >
            {msg.role === 'assistant' && (
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center text-sm border border-white/10 shrink-0 mt-1`}>
                {agent.emoji}
              </div>
            )}
            <div className="relative">
              <div
                className={`max-w-[75vw] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-indigo-500/20 text-white rounded-br-md'
                    : 'bg-white/5 text-gray-200 rounded-bl-md'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    className="prose prose-invert prose-sm max-w-none leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:mb-0.5 [&_code]:bg-white/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_pre]:bg-black/30 [&_pre]:p-3 [&_pre]:rounded-xl [&_pre]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-indigo-500/50 [&_blockquote]:pl-3 [&_blockquote]:text-gray-400 [&_blockquote]:italic [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-white/10 [&_th]:p-2 [&_th]:text-left [&_td]:border [&_td]:border-white/10 [&_td]:p-2 [&_table]:my-2 [&_a]:text-indigo-400 [&_a]:underline [&_a]:hover:text-indigo-300 [&_hr]:border-white/10 [&_hr]:my-3 [&_strong]:font-semibold [&_em]:italic [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_h1]:font-bold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:mb-2 [&_h2]:mb-1.5 [&_h3]:mb-1 [&_h1]:mt-2 [&_h2]:mt-2 [&_h3]:mt-1"
                  >
                    {msg.content}
                  </ReactMarkdown>
                ) : (
                  msg.content
                )}
              </div>

              {/* Action buttons on hover */}
              {hoveredMsgId === msg.id && (
                <div className={`absolute -top-2 ${
                  msg.role === 'user' ? 'left-0' : 'right-0'
                } flex items-center gap-0.5 z-10`}>
                  {/* Edit — только для user */}
                  {msg.role === 'user' && (
                    <button
                      onClick={() => {
                        setEditMessageId(msg.id)
                        setEditMessageContent(msg.content)
                        setInput(msg.content)
                        inputRef.current?.focus()
                      }}
                      className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                      title="Редактировать"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}

                  {/* Copy */}
                  <button
                    onClick={() => copyToClipboard(msg.content)}
                    className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                    title="Копировать"
                  >
                    <Copy className="w-3 h-3" />
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => deleteMessage(msg.id)}
                    className="p-1 rounded-md bg-gray-800 border border-white/10 text-red-400 hover:text-red-300 hover:bg-red-900/30 transition-all"
                    title="Удалить"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>

                  {/* Regenerate — только для assistant */}
                  {msg.role === 'assistant' && (
                    <button
                      onClick={() => regenerateMessage(msg)}
                      className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                      title="Перегенерировать"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  )}

                  {/* Speak — только для assistant */}
                  {msg.role === 'assistant' && msg.content && (
                    <button
                      onClick={() => speakText(msg.content, msg.id)}
                      className={`p-1 rounded-md border transition-all ${
                        isSpeaking && speakingMsgId === msg.id
                          ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400'
                          : 'bg-gray-800 border-white/10 text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                      title={isSpeaking && speakingMsgId === msg.id ? 'Остановить' : 'Озвучить ответ'}
                    >
                      {isSpeaking && speakingMsgId === msg.id ? (
                        <StopCircle className="w-3 h-3" />
                      ) : (
                        <Volume2 className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-sm shrink-0 mt-1">
                <User className="w-4 h-4 text-indigo-300" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center shrink-0 border border-white/10`}>
              {agent.emoji}
            </div>
            <div className="bg-white/5 rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Поле ввода */}
      <div className="border-t border-white/10 px-4 py-3 shrink-0">
        {/* Панель над полем ввода: статус и кнопки */}
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-2 text-[10px] text-gray-600">
            <div className="relative">
              <button
                onClick={() => setShowSettingsPanel(!showSettingsPanel)}
                className="p-1 rounded-md text-gray-500 hover:text-indigo-400 hover:bg-white/5 transition-all"
                title="Настройки чата"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
              {showSettingsPanel && (
                <ChatSettingsPanel
                  onImport={handleImportChats}
                  onClose={() => setShowSettingsPanel(false)}
                />
              )}
            </div>
            <span className="text-[10px] text-gray-500">{agent.model || 'gpt-5.4-mini'}</span>
            {webSearch && (
              <>
                
                <Globe className="w-3 h-3" />
                <span>Поиск вкл</span>
              </>
            )}
            {files.length > 0 && (
              <>
                
                <Paperclip className="w-3 h-3" />
                <span>{files.length} файл(ов)</span>
              </>
            )}
          </div>
          {/* Вторая строка: голос + агент + токены — на мобилке перенос */}
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
            <div className="relative">
              <button
                onClick={() => setShowVoiceMenu(!showVoiceMenu)}
                className="flex items-center gap-1 p-1 rounded-md hover:bg-white/5 transition-colors"
                title="Голос озвучки"
              >
                <Volume2 className="w-3 h-3 text-gray-500" />
                {selectedVoiceName && (
                  <span className="text-[9px] text-gray-500 max-w-[80px] overflow-hidden text-ellipsis whitespace-nowrap hidden sm:inline">
                    {selectedVoiceName.split('-').pop()?.replace(/[Nn]eural|[Nn]atural/g, '').trim() || selectedVoiceName.split(' ').pop()}
                  </span>
                )}
              </button>
              {showVoiceMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowVoiceMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-1 w-56 glass-card z-20 p-1.5 space-y-0.5 max-h-48 overflow-y-auto">
                    {availableVoices.length === 0 ? (
                      <div className="px-2 py-1.5 text-[10px] text-gray-500">Нет голосов</div>
                    ) : (
                      availableVoices.map((v) => (
                        <button
                          key={v.name}
                          onClick={() => {
                            setSelectedVoiceName(v.name)
                            setShowVoiceMenu(false)
                          }}
                          className={`w-full text-left px-2 py-1.5 rounded-md text-[10px] transition-colors ${
                            selectedVoiceName === v.name
                              ? 'bg-indigo-500/10 text-indigo-300'
                              : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate flex-1">{v.name}</span>
                            <span className="text-[8px] text-gray-600 ml-1">{v.lang}</span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            <span className="text-gray-500">агент</span>
            <span className="text-amber-400/80 font-medium">{balance.toLocaleString()}</span>
            <span className="text-gray-600">токенов</span>
          </div>
        </div>

        {/* Edit mode indicator */}
        {editMessageId && (
          <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <Pencil className="w-3.5 h-3.5 text-indigo-400" />
            <span className="text-xs text-indigo-300">Редактирование сообщения</span>
            <button
              onClick={() => { setEditMessageId(null); setEditMessageContent(''); setInput('') }}
              className="ml-auto p-0.5 rounded hover:bg-indigo-500/20 text-indigo-400"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Input field */}
        <div className="glass rounded-2xl p-1.5 border border-white/5 focus-within:border-indigo-500/30 transition-all">
          <div className="flex items-end gap-2">
            {/* Voice input button */}
            <button
              onClick={startRecording}
              className={`p-2 rounded-lg transition-all shrink-0 ${
                isRecording
                  ? 'bg-red-500/20 text-red-400 animate-pulse'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
              }`}
              title={isRecording ? 'Остановить запись' : 'Голосовой ввод'}
            >
              {isRecording ? <StopCircle className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>

            {/* Attach file button with menu */}
            <div className="relative">
              <button
                onClick={() => setShowAttachMenu(prev => !prev)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-all shrink-0"
                title="Прикрепить"
              >
                <Paperclip className="w-4 h-4" />
              </button>

              {showAttachMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAttachMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-2 w-52 bg-[#1a1a2e] border border-white/10 rounded-xl z-20 p-1.5 space-y-0.5 shadow-xl">
                    <button
                      onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                    >
                      <Upload className="w-4 h-4 text-indigo-400" />
                      <span>Загрузить файлы</span>
                    </button>
                    <button
                      onClick={() => { handleCaptureCamera(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                    >
                      <Camera className="w-4 h-4 text-purple-400" />
                      <span>Захват камерой</span>
                    </button>
                    <button
                      onClick={() => { handleAddPageLink(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                    >
                      <Link className="w-4 h-4 text-blue-400" />
                      <span>Добавить ссылку на страницу</span>
                    </button>
                  </div>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              multiple
              accept="*/*"
            />
            <input
              ref={fileImportRef}
              type="file"
              onChange={handleFileImport}
              className="hidden"
              accept=".json"
            />

            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Спроси ${agent.name}...`}
              rows={1}
              className="flex-1 bg-transparent px-1 py-2 text-sm text-gray-200 placeholder-gray-600 resize-none max-h-[260px] outline-none"
            />

            <div className="flex items-center gap-1 pb-1 pr-1">
              <button
                onClick={() => setWebSearch(!webSearch)}
                className={`
                  p-2 rounded-lg transition-all
                  ${webSearch
                    ? 'bg-indigo-500/20 text-indigo-400'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                  }
                `}
                title="Поиск в интернете"
              >
                <Globe className="w-4 h-4" />
              </button>

              {isSending ? (
                <button
                  onClick={handleStop}
                  className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                  title="Остановить"
                >
                  <StopCircle className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={(!input.trim() && files.length === 0)}
                  className="p-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Подсказка о загруженных файлах */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <Upload className="w-3 h-3 text-indigo-400" />
                <span className="text-[10px] text-indigo-300 truncate max-w-[120px]">{f.name}</span>
                <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="p-0.5 hover:text-white text-indigo-400">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center mt-2">
          <button
            onClick={handleClearDialogue}
            disabled={messages.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors text-xs text-gray-400"
          >
            <Trash2 className="w-3 h-3" />
            Очистить диалог
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Agent Card ───────────────────────────────────────────

function AgentCard({
  agent,
  onClick,
  onDelete,
}: {
  agent: AgentInfo
  onClick: () => void
  onDelete?: () => void
}) {
  const colorClass = AGENT_COLORS[agent.color] || AGENT_COLORS.indigo

  return (
    <div
      className={`glass-card p-5 group cursor-pointer hover:scale-[1.02] transition-all duration-200 border ${colorClass} relative overflow-hidden`}
      onClick={onClick}
    >
      {/* Glow эффект */}
      <div className={`absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br ${colorClass} rounded-full blur-3xl opacity-[0.08] group-hover:opacity-[0.15] transition-opacity`} />

      <div className="flex items-start gap-4 relative">
        {agent.avatar_url ? (
          <img src={agent.avatar_url} alt="" className="w-14 h-14 rounded-xl object-cover border border-white/10 shrink-0" />
        ) : (
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center text-2xl shrink-0 border border-white/10`}>
            {agent.emoji}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-white group-hover:text-indigo-300 transition-colors flex items-center gap-2">
            {agent.name}
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
              agent.provider === 'typegpt'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                : 'bg-amber-500/20 text-amber-400 border border-amber-500/20'
            }`}>
              {agent.provider === 'typegpt' ? 'FREE' : '👑'}
            </span>
          </h2>
          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{agent.description}</p>

          {/* Скиллы */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {agent.skills.map((skill, i) => (
              <div
                key={i}
                className="px-2 py-0.5 rounded-lg bg-white/5 border border-white/5 flex items-center gap-1"
                title={skill.description}
              >
                <SkillIcon name={skill.icon} className="w-3 h-3 text-indigo-400" />
                <span className="text-[10px] text-gray-500">{skill.name}</span>
              </div>
            ))}
          </div>

          {/* Инструменты */}
          {agent.tools && agent.tools.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-white/5">
              {agent.tools.slice(0, 4).map((tool, i) => (
                <div key={i} className="px-2 py-0.5 rounded-lg bg-indigo-500/5 border border-indigo-500/10 flex items-center gap-1" title={tool.description}>
                  <Zap className="w-2.5 h-2.5 text-amber-400" />
                  <span className="text-[10px] text-amber-300/70">{tool.name}</span>
                </div>
              ))}
              {agent.tools.length > 4 && (
                <div className="px-2 py-0.5 rounded-lg bg-white/5 text-[10px] text-gray-500">+{agent.tools.length - 4}</div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
            <button className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 transition-colors">
              <MessageCircle className="w-3.5 h-3.5" />
              Начать диалог
            </button>
            <Sparkles className="w-3 h-3 text-indigo-500/50" />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Create Agent Modal ────────────────────────────────────
function CreateAgentModal({ onClose, onCreated }: { onClose: () => void; onCreated: (agent: AgentInfo) => void }) {
  const { balance } = useTokenStore()
  const isPaidLocked = (balance ?? 0) < 1000
  const [name, setName] = useState('')
  const [emoji, setEmoji] = useState('🤖')
  const [description, setDescription] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [paidModels, setPaidModels] = useState<{model_name: string; display_name?: string; is_paid: boolean}[]>([])
  const [model, setModel] = useState('')
  const [provider, setProvider] = useState('openrouter')
  const [saving, setSaving] = useState(false)
  const [selectedToolNames, setSelectedToolNames] = useState<string[]>(['search_web', 'fetch_url', 'analyze_text', 'format_json'])
  const AVAILABLE_TOOLS = ['search_web', 'fetch_url', 'analyze_text', 'format_json', 'parse_google_doc']

  const emojis = ['🤖', '🧠', '⚡', '🎯', '🔮', '🌟', '💡', '🦾', '🎨', '📊', '🛡️', '🚀']

  // Загружаем платные модели
  useEffect(() => {
    client.get('/admin/ai-models').then(({ data }) => {
      const paid = data.filter((m: any) => m.category === 'chat' && m.is_paid)
      setPaidModels(paid)
      if (paid.length > 0 && !model) {
        setModel(paid[0].model_name)
      }
    }).catch(() => {})
  }, [])

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Введите имя агента'); return }
    if ((balance ?? 0) < 1000) { toast.error('Недостаточно токенов. Пополните баланс для доступа к платным моделям.'); return }
    setSaving(true)
    try {
      const { data } = await client.post('/agents', {
        name: name.trim(),
        emoji,
        color: 'indigo',
        description: description.trim(),
        long_description: '',
        system_prompt: systemPrompt.trim(),
        model: model || paidModels[0]?.model_name || 'openai/gpt-5.4-mini',
        provider: 'openrouter',
        skills: [],
      })
      // Добавляем инструменты
      for (const toolName of selectedToolNames) {
        await client.post(`/agents/${data.id}/tools`, {
          name: toolName,
          description: toolName === 'search_web' ? 'Search the web for current information' :
                       toolName === 'fetch_url' ? 'Fetch and extract content from a URL' :
                       toolName === 'analyze_text' ? 'Analyze text: sentiment, keywords, summary' :
                       'Format and validate JSON data',
          handler: toolName,
          parameters: '{}',
          is_active: true,
        })
      }
      onCreated(data)
      toast.success('Агент создан!')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка создания')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg glass-card p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-indigo-400" />
            Создать агента
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Имя агента *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Мой агент"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Иконка</label>
            <div className="flex flex-wrap gap-2">
              {emojis.map(e => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg transition-all ${
                    emoji === e
                      ? 'bg-indigo-500/20 border border-indigo-500/50 scale-110'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >{e}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Кратко опиши, чем занимается агент"
              rows={2}
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Инструкция (system prompt)</label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Опиши поведение агента, его специализацию..."
              rows={4}
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">
              Модель (платная)
              {isPaidLocked && <span className="text-[11px] ml-1">🔒</span>}
              {isPaidLocked && <span className="text-[10px] ml-1.5 text-red-400">(недостаточно токенов)</span>}
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={isPaidLocked}
              className={`w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-indigo-500/50 ${isPaidLocked ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {paidModels.length === 0 ? (
                <option value="" className="text-gray-500">Нет доступных платных моделей</option>
              ) : (
                paidModels.map((m) => (
                  <option key={m.model_name} value={m.model_name}>
                    {isPaidLocked ? '🔒 ' : ''}{m.display_name || m.model_name} 👑
                  </option>
                ))
              )}
            </select>
            <p className="text-[10px] text-gray-600 mt-1">Пользовательские агенты работают только на платных моделях</p>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Инструменты</label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_TOOLS.map(toolName => (
                <label
                  key={toolName}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-[11px] cursor-pointer transition-all ${
                    selectedToolNames.includes(toolName)
                      ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300'
                      : 'border-white/10 bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedToolNames.includes(toolName)}
                    onChange={() => setSelectedToolNames(prev =>
                      prev.includes(toolName)
                        ? prev.filter(n => n !== toolName)
                        : [...prev, toolName]
                    )}
                    className="sr-only"
                  />
                  <Zap className={`w-3 h-3 ${selectedToolNames.includes(toolName) ? 'text-amber-400' : 'text-gray-600'}`} />
                  {toolName}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-gray-600 mt-1">Выберите, какие возможности будут у агента</p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-white/10 text-sm text-gray-300 hover:bg-white/5 transition-colors">Отмена</button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-sm font-medium text-white hover:from-indigo-500 hover:to-purple-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Создание...
              </span>
            ) : (
              'Создать агента'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Agents Page ─────────────────────────────────────

export default function Agents() {
  const [agents, setAgents] = useState<AgentInfo[]>(FALLBACK_AGENTS)
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm('Удалить этого агента?')) return
    try {
      await client.delete('/agents/' + agentId)
      setAgents(prev => prev.filter(a => a.id !== agentId))
      toast.success('Агент удалён')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка удаления')
    }
  }

  // Загрузка агентов с сервера (когда будет API)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await client.get('/agents')
        if (!cancelled && data?.length > 0) {
          setAgents(data)
        }
      } catch {
        // fallback data already set
      }
      if (!cancelled) setLoading(false)
    })()
    return () => { cancelled = true }
  }, [])

  // Фильтрация
  const filteredAgents = searchQuery.trim()
    ? agents.filter(a =>
        a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.skills.some(s =>
          s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : agents

  if (selectedAgent) {
    return (
      <AgentChat
        agent={selectedAgent}
        onBack={() => {
          setSelectedAgent(null)
          // TODO: restore reference
        }}
      />
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Заголовок */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
            <Bot className="w-6 h-6" />
            ИИ-Агенты
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Интеллектуальные агенты с разными специализациями. Каждый — эксперт в своей области.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-sm font-medium text-white transition-all shadow-lg shadow-indigo-600/20"
        >
          <Plus className="w-4 h-4" />
          Создать
        </button>
      </div>

      {/* Поиск */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Поиск агентов по имени, описанию или навыкам..."
          className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 outline-none focus:border-indigo-500/50 transition-colors"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
        </div>
      ) : (
        <>
          {filteredAgents.length === 0 ? (
            <div className="text-center py-12">
              <Bot className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                {searchQuery ? 'Агенты по вашему запросу не найдены' : 'Агенты пока не добавлены'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-3 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-gray-300 transition-colors"
                >
                  Сбросить поиск
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAgents.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  onClick={() => {
                    localStorage.setItem('selected_agent_id', agent.id)
                    setSelectedAgent(agent)
                  }}
                  onDelete={agent.is_global ? undefined : () => handleDeleteAgent(agent.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Модалка создания агента */}
      {showCreateModal && (
        <CreateAgentModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(agent) => {
            setAgents(prev => [...prev, agent])
            setShowCreateModal(false)
          }}
        />
      )}

      {/* Section: Сравнение с обычными ботами */}
      <div className="pt-6 border-t border-white/5 mt-8">
        <div className="bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5 rounded-2xl border border-white/5 p-6">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            Чем агенты отличаются от обычных чат-моделей?
          </h3>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div className="p-3 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-semibold text-white">Специализация</span>
              </div>
              <p className="text-[11px] text-gray-400">Каждый агент — эксперт в своей области с настроенным поведением и инструментами</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-semibold text-white">Инструменты</span>
              </div>
              <p className="text-[11px] text-gray-400">Агенты могут выполнять реальные действия: анализировать код, проверять SEO, искать данные</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-semibold text-white">Контекст</span>
              </div>
              <p className="text-[11px] text-gray-400">Агенты помнят контекст задачи и могут последовательно выполнять многошаговые действия</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}